/**
 * Which labels the view can show at once, and where.
 *
 * A network of a few hundred marks cannot name all of them: at the densities a real viewpoint
 * reaches, every label overlaps two others and the text becomes a grey mat over the picture. So
 * this is a declutter rather than a projection - it decides what is worth saying, and the caller
 * draws exactly what it returns.
 *
 * Pure, and in screen space, for the reason the hit-tests are: it has to agree with what was
 * drawn rather than approximate it, and it is the part with a wrong answer in it. The caller holds
 * the DOM.
 */

import {
  applyViewProjection,
  clipToScreen,
  toViewProjectionMatrix,
} from '../../geometry/viewProjection';
import type { Size, ViewCamera } from '../../types';
import type { NetworkGraph, NetworkLayout } from '../types';
import {
  NODE_FLOATS,
  VOLUME_FLOATS,
  type NetworkInstances,
} from './packNetwork';
import type { NetworkFocus } from './packNetwork';

/** What the caller draws, one per label it should show. */
export type NetworkLabelPlacement = {
  /**
   * Stable across frames, so the caller's list reconciles rather than remounting.
   *
   * The node's own id rather than its index: an index is a position in an array the next rebuild
   * reorders, and a label that remounts every frame cannot be transitioned or selected.
   */
  key: string;
  text: string;
  /** Anchor in CSS pixels within the canvas. The caller centres the label on it. */
  x: number;
  y: number;
  /**
   * Whether this names a region or a mark.
   *
   * Carried so the caller can weight them - a region is the structure a reader is looking for and
   * reads as a heading, a mark is one of many - rather than having to work it out again from the
   * graph.
   */
  kind: 'region' | 'mark';
};

/**
 * Roughly how wide a character is, as a fraction of the font size.
 *
 * An estimate, deliberately, because the alternative is a DOM measurement and this function has
 * no DOM. It is only ever used to reject overlaps, where being a few pixels out costs a label
 * that would have fitted or keeps one that slightly touches - and never to size anything, since
 * the caller lets the real font decide that.
 *
 * Tuned a little wide. A label dropped for a collision it did not quite have is invisible; two
 * labels overlapping by three pixels is the thing this exists to prevent.
 */
const CHARACTER_WIDTH_RATIO = 0.58;

/** Between a mark and its label, in pixels, so the text does not sit on the sphere. */
const LABEL_GAP = 5;

/** Kept clear of the canvas edge, so a label is never half out of the pane. */
const EDGE_INSET = 4;

/**
 * Breathing room around a label's box, in pixels, beyond the ink it is estimated to cover.
 *
 * Zero would let two labels sit exactly shoulder to shoulder, which passes a non-overlap test and
 * still reads as one block of text - two component names a line apart on a dense estate are
 * legible individually and a mat together. Costs the second of each such pair, which is the
 * trade this module exists to make.
 */
const COLLISION_PADDING = 4;

/**
 * Below this drawn radius a mark does not get a label.
 *
 * Not a performance guard. A mark projecting to two pixels is one of hundreds at that distance,
 * and naming an arbitrary few of them says less than naming none - the reader cannot tell which
 * sphere each label belongs to, and the cap would spend itself on them before reaching the marks
 * in front.
 */
const SMALLEST_LABELLED_MARK = 3;

/**
 * What a candidate beats on a collision, as one number.
 *
 * A tier times a wide stride, plus the drawn radius within the tier. One number rather than a
 * comparator that special-cases the focus: `Array.prototype.sort` is only defined for a
 * *consistent* comparator, and one that answers "a first" for the focused candidate whichever
 * side it is passed on is not - which left the focus losing or winning depending on how many
 * candidates there were and which order the engine compared them in.
 */
const PRIORITY_TIER = 1e6;
const TIER_MARK = 0;
const TIER_REGION = 1;
const TIER_FOCUS = 2;

type Candidate = NetworkLabelPlacement & {
  /** Larger wins a collision - see `PRIORITY_TIER`. */
  priority: number;
  width: number;
  height: number;
};

const toBoxWidth = (
  text: string,
  fontSize: number,
  maximumWidth: number
): number =>
  Math.min(maximumWidth, text.length * fontSize * CHARACTER_WIDTH_RATIO);

const overlaps = (a: Candidate, b: Candidate): boolean =>
  Math.abs(a.x - b.x) * 2 < a.width + b.width + COLLISION_PADDING * 2 &&
  Math.abs(a.y - b.y) * 2 < a.height + b.height + COLLISION_PADDING * 2;

export type PlaceNetworkLabelsArgs = {
  graph: NetworkGraph;
  /** The layout as *drawn* - see `Network3DView`'s `drawnLayout`. */
  layout: NetworkLayout;
  /** For the radius each mark and region was drawn at, which the labels sit clear of. */
  instances: NetworkInstances;
  camera: ViewCamera;
  /** CSS pixels, which is what the placements are in. */
  viewport: Size;
  /**
   * What the reader is pointing at, whose label is kept whatever it collides with.
   *
   * A hover that dropped its own label - because a bigger mark beside it had already claimed the
   * space - would read as the view declining to answer the one question being asked of it.
   */
  focus: NetworkFocus | null;
  /** The most labels to return, which bounds the caller's DOM as well as the clutter. */
  maximum: number;
  fontSize: number;
  /**
   * Where the caller truncates a label, in pixels, so an estimated box stops growing with it.
   *
   * Taken rather than held here, as the font size is: the caller's CSS is what truncates, and two
   * copies of the number would diverge silently - leaving the estimate wider or narrower than the
   * ink and the declutter rejecting labels it could have kept.
   */
  maximumWidth: number;
};

/**
 * The labels to draw, in the order they were accepted.
 *
 * Regions before marks, then larger before smaller, and the focus before either. Regions first
 * because they are the structure: on a grouped viewpoint five named clouds make the picture
 * legible, and the same five labels spent on whichever marks happened to be nearest would not.
 * Larger before smaller because a mark's drawn size is how near and how important it is, so a
 * collision should be won by the one the reader is more likely to be reading.
 */
export const placeNetworkLabels = ({
  graph,
  layout,
  instances,
  camera,
  viewport,
  focus,
  maximum,
  fontSize,
  maximumWidth,
}: PlaceNetworkLabelsArgs): NetworkLabelPlacement[] => {
  if (viewport.width === 0 || viewport.height === 0) return [];
  if (maximum <= 0) return [];

  const matrix = toViewProjectionMatrix(camera, viewport);
  const pixelsPerUnit =
    (Math.min(viewport.width, viewport.height) / 2) * camera.zoom;
  const height = fontSize * 1.35;

  const toCandidate = (
    key: string,
    text: string,
    worldX: number,
    worldY: number,
    worldZ: number,
    worldRadius: number,
    kind: 'region' | 'mark',
    tier: number
  ): Candidate | null => {
    const isFocused = tier === TIER_FOCUS;
    if (text === '') return null;
    const clip = applyViewProjection(matrix, worldX, worldY, worldZ);
    /* Behind the eye: its projection wraps around the origin, so the anchor would be somewhere
       the thing itself is not. */
    if (clip.w <= 0) return null;

    const { screenX, screenY } = clipToScreen(clip, viewport);
    const drawn = (worldRadius * pixelsPerUnit) / clip.w;
    /* The floor does not apply to what the reader is pointing at or has focused. It exists so the
       budget is not spent naming an arbitrary few of the hundreds of marks at that distance - and
       a mark someone has singled out is not arbitrary. Without this the focus tier never got the
       chance to win, since the candidate was dropped before it was ranked. */
    if (!isFocused && kind === 'mark' && drawn < SMALLEST_LABELLED_MARK) {
      return null;
    }

    const width = toBoxWidth(text, fontSize, maximumWidth);
    /* A region is named above itself, where the cloud is thinnest and no mark sits; a mark below,
       which is where a reader looks for a caption and leaves the sphere itself uncovered. */
    const anchorY =
      kind === 'region'
        ? screenY - drawn - LABEL_GAP - height / 2
        : screenY + drawn + LABEL_GAP + height / 2;

    /* Clamped rather than dropped. A region can be wider than the pane, so its top edge is often
       off-screen while the region itself is most of the picture - and a mark at the bottom edge
       is still worth naming. */
    const x = Math.min(
      Math.max(screenX, EDGE_INSET + width / 2),
      viewport.width - EDGE_INSET - width / 2
    );
    const y = Math.min(
      Math.max(anchorY, EDGE_INSET + height / 2),
      viewport.height - EDGE_INSET - height / 2
    );

    /* Off the pane entirely, which the clamp cannot rescue: the thing is not on screen at all. */
    if (screenX < -drawn || screenX > viewport.width + drawn) return null;
    if (screenY < -drawn || screenY > viewport.height + drawn) return null;

    return {
      key,
      text,
      x,
      y,
      kind,
      width,
      height,
      priority: tier * PRIORITY_TIER + drawn,
    };
  };

  /**
   * Which mark the focus forces a label for, if any.
   *
   * Both ends of a focused reference, because that is what the focus is about - and a label on
   * one end alone would be worse than neither, since the reader could not tell which end it
   * named. A set rather than a key for the same reason.
   *
   * `index` means a different array per kind, which is why this branches rather than reading
   * `focus.index` once: a route index read as a volume index names some unrelated region.
   */
  const toFocusedKeys = (): ReadonlySet<string> => {
    if (focus === null) return new Set();
    if (focus.kind === 'node') {
      const id = graph.nodes[focus.index]?.id;
      return id === undefined ? new Set() : new Set([id]);
    }
    if (focus.kind === 'edge') {
      const link = layout.links[focus.index];
      return new Set(
        [
          graph.nodes[link?.source ?? -1]?.id,
          graph.nodes[link?.target ?? -1]?.id,
        ].filter((id): id is string => id !== undefined)
      );
    }
    /* Every result named, which is most of what a search is for: a lit mark the reader still has
       to hover to identify has only half answered them. */
    if (focus.kind === 'marks') {
      return new Set(
        focus.indices
          .map(index => graph.nodes[index]?.id)
          .filter((id): id is string => id !== undefined)
      );
    }

    const id = graph.nodes[layout.volumes[focus.index]?.nodeIndex ?? -1]?.id;
    return id === undefined ? new Set() : new Set([id]);
  };

  const focusedKeys = toFocusedKeys();

  const toTier = (key: string, kind: 'region' | 'mark'): number =>
    focusedKeys.has(key)
      ? TIER_FOCUS
      : kind === 'region'
        ? TIER_REGION
        : TIER_MARK;

  const candidates: Candidate[] = [];

  layout.volumes.forEach(({ center, nodeIndex }, volumeIndex) => {
    const node = graph.nodes[nodeIndex];
    if (node === undefined) return;
    const at = volumeIndex * VOLUME_FLOATS;
    const candidate = toCandidate(
      node.id,
      node.label,
      center[0],
      center[1],
      center[2],
      instances.volumes[at + 3] ?? 0,
      'region',
      toTier(node.id, 'region')
    );
    if (candidate) candidates.push(candidate);
  });

  for (let index = 0; index < instances.nodeCount; index++) {
    const node = graph.nodes[index];
    const position = layout.positions[index];
    if (node === undefined || position === undefined) continue;
    /* A weightless node is a region, drawn as nothing - its label comes from the volume. */
    if (node.weight === 0) continue;

    const candidate = toCandidate(
      node.id,
      node.label,
      position[0],
      position[1],
      position[2],
      instances.nodes[index * NODE_FLOATS + 3] ?? 0,
      'mark',
      toTier(node.id, 'mark')
    );
    if (candidate) candidates.push(candidate);
  }

  const ordered = candidates.sort((a, b) => b.priority - a.priority);

  const placed: Candidate[] = [];
  for (const candidate of ordered) {
    if (placed.length >= maximum) break;
    if (placed.some(other => overlaps(candidate, other))) continue;
    placed.push(candidate);
  }

  return placed.map(({ key, text, x, y, kind }) => ({ key, text, x, y, kind }));
};
